/**
 * BloodworkHomeCard
 *
 * Small, always-visible entry point on the Home tab for the Bloodwork feature.
 * - Genius / trialing: shows lab summary + Auto-Sync focus one-liner if active
 * - Free / Smart: shows a Genius upsell card
 * Clicking navigates to the MIQ tab where the full Bloodwork section lives.
 */

import React, { useEffect, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BloodworkHomeCard = ({ userId, isPremium, userTier, onNavigateToMIQ, onUpgradeGenius }) => {
  const [latest, setLatest] = useState(null);
  const [focus, setFocus] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const isGenius = isPremium || userTier === 'genius' || userTier === 'miq';

  useEffect(() => {
    if (!userId) return;
    if (!isGenius) { setLoaded(true); return; }
    (async () => {
      try {
        const [lRes, fRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/biomarkers/latest/${userId}`).then((r) => r.ok ? r.json() : null),
          fetch(`${BACKEND_URL}/api/bloodwork-nutrition-focus/${userId}`).then((r) => r.ok ? r.json() : null),
        ]);
        setLatest(lRes);
        setFocus(fRes);
      } catch (_e) { /* silent */ }
      setLoaded(true);
    })();
  }, [userId, isGenius]);

  const handleClick = () => {
    if (!isGenius && typeof onUpgradeGenius === 'function') {
      onUpgradeGenius();
      return;
    }
    if (typeof onNavigateToMIQ === 'function') onNavigateToMIQ();
  };

  // ─────────── LOCKED (Free / Smart) ───────────
  if (!isGenius) {
    return (
      <div
        data-testid="bloodwork-home-card-locked"
        onClick={handleClick}
        style={{
          background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.06) 0%, rgba(147, 51, 234, 0.05) 100%)',
          borderRadius: '16px',
          padding: '16px',
          border: '1px solid rgba(220, 38, 38, 0.18)',
          cursor: 'pointer',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <div style={{
          width: '44px', height: '44px', borderRadius: '12px',
          background: 'linear-gradient(135deg, #dc2626 0%, #9333ea 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '22px', flexShrink: 0,
          boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
        }}>🩸</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '2px' }}>
            Bloodwork · Locked
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
            Upload lab PDFs and get personalized nutrition tuned to your biomarkers · <span style={{ color: '#9333ea', fontWeight: 600 }}>Unlock with Genius →</span>
          </div>
        </div>
      </div>
    );
  }

  // ─────────── EMPTY (Genius, no entries yet) ───────────
  if (loaded && latest && !latest.has_data) {
    return (
      <div
        data-testid="bloodwork-home-card-empty"
        onClick={handleClick}
        style={{
          background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.08) 0%, rgba(147, 51, 234, 0.06) 100%)',
          borderRadius: '16px',
          padding: '16px',
          border: '1px solid rgba(220, 38, 38, 0.2)',
          cursor: 'pointer',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <div style={{
          width: '44px', height: '44px', borderRadius: '12px',
          background: 'linear-gradient(135deg, #dc2626 0%, #9333ea 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '22px', flexShrink: 0,
          boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
        }}>🩸</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '2px' }}>
            Add your first lab report
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
            Tap to upload a PDF or photo — AI reads your markers and personalizes your plan
          </div>
        </div>
        <span style={{ fontSize: '18px', color: 'var(--text-secondary)' }}>›</span>
      </div>
    );
  }

  // ─────────── ACTIVE (Genius, has data) ───────────
  if (loaded && latest?.has_data) {
    const oor = latest.out_of_range_count ?? 0;
    const total = latest.marker_count ?? 0;
    const labDate = latest.entry?.lab_date || latest.entry?.created_at?.slice(0, 10) || '';
    return (
      <div
        data-testid="bloodwork-home-card"
        onClick={handleClick}
        style={{
          background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.08) 0%, rgba(147, 51, 234, 0.06) 100%)',
          borderRadius: '16px',
          padding: '16px',
          border: '1px solid rgba(220, 38, 38, 0.2)',
          cursor: 'pointer',
          marginBottom: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '44px', height: '44px', borderRadius: '12px',
            background: 'linear-gradient(135deg, #dc2626 0%, #9333ea 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '22px', flexShrink: 0,
            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
          }}>🩸</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
              <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>Bloodwork</span>
              {oor > 0 && (
                <span style={{
                  fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
                  padding: '2px 8px', borderRadius: '999px',
                  background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                }}>{oor} out of range</span>
              )}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
              {total} markers · Last labs {labDate}
            </div>
          </div>
          <span style={{ fontSize: '18px', color: 'var(--text-secondary)' }}>›</span>
        </div>

        {focus?.active && (
          <div style={{
            marginTop: '10px', padding: '8px 10px',
            background: 'rgba(147, 51, 234, 0.1)',
            border: '1px solid rgba(147, 51, 234, 0.25)',
            borderRadius: '8px',
            fontSize: '11px', color: 'var(--text-primary)', lineHeight: 1.4,
          }}>
            <span style={{ fontWeight: 700, color: '#9333ea' }}>🎯 Auto-Sync active · </span>
            {focus.labels?.join(' + ')} · {focus.days_remaining} days left
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default BloodworkHomeCard;
